import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { type FC, memo, useEffect, useState } from "react";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekViewContext } from "@/contexts/WeekView";
import { HOSPITAL_DEMO } from "@/theme/tokens";
import type { ShiftCounts, WeekData } from "@/utils/models";
import { calculatePercantage } from "@/utils/utilFunctions";
import Style from "./Staffing.module.scss";

const SCHEDULING_TITLES = ["At Target", "Understaffed", "Overstaffed"] as const satisfies readonly string[];

type CircleProps = {
	index: number;
	percentageResults?: ShiftCounts;
	isSmall?: boolean;
};

const CustomCircularProgress: FC<CircleProps> = ({ percentageResults, index, isSmall }) => {
	// Only hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useHospitalData();
	const value =
		index === 0
			? Number.parseInt(percentageResults?.atTarget || "0", 10)
			: index === 1
				? Number.parseInt(percentageResults?.understaffed || "0", 10)
				: Number.parseInt(percentageResults?.overstaffed || "0", 10);

	return (
		<Box
			sx={{
				position: "relative",
				display: "inline-flex",
			}}
		>
			<CircularProgress variant="determinate" value={100} size={isSmall ? 30 : 40} thickness={4} sx={{ color: "grey.300" }} />
			<CircularProgress
				variant="determinate"
				value={value}
				size={isSmall ? 30 : 40}
				thickness={4}
				// Demo: green ring; others keep the theme secondary color.
				sx={{ color: isHospitalDemoView ? HOSPITAL_DEMO.GREEN : "secondary.main", position: "absolute", left: 0, top: 0 }}
			/>
		</Box>
	);
};

type AvailabilityProps = {
	setAvailabilityLoaded: (loaded: boolean) => void;
	isSmall?: boolean;
};

export const Availability: FC<AvailabilityProps> = memo(({ isSmall, setAvailabilityLoaded }) => {
	const { isHospital, chosenDepartment } = useHospitalData();
	const { scheduleWeeks, scheduleWeekIndex, scheduleNurses, isLoading } = useWeekViewContext();
	const shownWeek: WeekData | undefined = scheduleWeeks[scheduleWeekIndex];
	const [percantageResults, setPercantageResults] = useState<ShiftCounts>();

	const getFractionText = (title: string) => {
		const count =
			title === "At Target"
				? percantageResults?.atTargetNumber
				: title === "Understaffed"
					? percantageResults?.understaffedNumber
					: percantageResults?.overstaffedNumber;
		return `${count}/${percantageResults?.totalNumber}`;
	};

	useEffect(() => {
		// A week still loading counts nothing yet, nor one whose roster is: the count goes by each person's role.
		if (!shownWeek || isLoading) return;
		setPercantageResults(calculatePercantage(shownWeek, scheduleNurses, chosenDepartment, isHospital));
	}, [shownWeek, scheduleNurses, isLoading, isHospital, chosenDepartment]);

	if (percantageResults) setAvailabilityLoaded(true);

	return (
		<>
			{isSmall ? (
				<div className={Style.avContainer}>
					<div className={Style.schContainer}>
						<Grid
							container
							direction="row"
							wrap="nowrap"
							sx={{
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{SCHEDULING_TITLES.map((title, index) => {
								return (
									<Grid key={index.toString()}>
										<Tooltip title={getFractionText(title)} arrow>
											<Grid
												container
												sx={{
													flexDirection: "column",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												<Box sx={{ position: "relative", display: "inline-flex" }}>
													<CustomCircularProgress percentageResults={percantageResults} index={index} isSmall={isSmall} />
													<Box
														sx={{
															top: 0,
															left: 0,
															bottom: 0,
															right: 0,
															position: "absolute",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														<Typography variant="caption" component="div">
															{index === 0
																? Number.parseInt(percantageResults?.atTarget || "", 10)
																: index === 1
																	? Number.parseInt(percantageResults?.understaffed || "", 10)
																	: Number.parseInt(percantageResults?.overstaffed || "", 10)}
															%
														</Typography>
													</Box>
												</Box>
												<Box
													component="span"
													sx={{
														margin: "0px 5px",
														padding: 0,
														fontSize: "12px",
														whiteSpace: "nowrap",
													}}
												>
													{title}
												</Box>
											</Grid>
										</Tooltip>
									</Grid>
								);
							})}
						</Grid>
					</div>
				</div>
			) : (
				<div className={Style.avContainer}>
					<div className={Style.schContainer}>
						<Stack
							direction="row"
							spacing={2}
							sx={{
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{SCHEDULING_TITLES.map((title, index) => {
								return (
									<Box key={index.toString()}>
										<Tooltip title={getFractionText(title)} arrow>
											<Stack
												direction="column"
												sx={{
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												{/* nowrap: "At Target" used to break onto two lines and push the circle below the toolbar */}
												<span style={{ fontSize: "14px", whiteSpace: "nowrap" }}>{title}</span>
												<Box sx={{ position: "relative", display: "inline-flex" }}>
													<CustomCircularProgress percentageResults={percantageResults} index={index} isSmall={isSmall} />
													<Box
														sx={{
															top: 0,
															left: 0,
															bottom: 0,
															right: 0,
															position: "absolute",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														<Typography variant="caption" component="div">
															{index === 0
																? Number.parseInt(percantageResults?.atTarget || "", 10)
																: index === 1
																	? Number.parseInt(percantageResults?.understaffed || "", 10)
																	: Number.parseInt(percantageResults?.overstaffed || "", 10)}
															%
														</Typography>
													</Box>
												</Box>
											</Stack>
										</Tooltip>
									</Box>
								);
							})}
						</Stack>
					</div>
				</div>
			)}
		</>
	);
});
